// =============================================================================
// GOUVERNANCE - Workflows Versionnés
// Interface de visualisation et gestion des workflows métier
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  GitBranch,
  Play,
  Pause,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Settings,
  Eye,
  Edit,
  Trash2,
  Plus,
  RefreshCw,
  Shield,
  Clock,
  ArrowRight,
  Loader2,
  FileText,
  Users,
  Activity,
  Copy
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../../components/ui/collapsible";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Couleurs des statuts de workflow
const STATUS_COLORS = {
  active: { bg: "bg-green-500/20", text: "text-green-400", border: "border-green-500/50", label: "Actif" },
  draft: { bg: "bg-amber-500/20", text: "text-amber-400", border: "border-amber-500/50", label: "Brouillon" },
  validated: { bg: "bg-blue-500/20", text: "text-blue-400", border: "border-blue-500/50", label: "Validé" },
  archived: { bg: "bg-slate-500/20", text: "text-slate-400", border: "border-slate-500/50", label: "Archivé" }
};

// Couleurs des phases
const PHASE_COLORS = {
  commercial: "bg-blue-500",
  administratif: "bg-amber-500",
  technique: "bg-purple-500", 
  conformité: "bg-green-500",
  facturation: "bg-emerald-500",
  termine: "bg-slate-500"
};

export default function GouvWorkflows({ tenantId }) {
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [activeWorkflow, setActiveWorkflow] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowType, setWorkflowType] = useState("all");
  const [expandedStatuts, setExpandedStatuts] = useState({});
  const [gardeFousCatalogue, setGardeFousCatalogue] = useState([]);
  const [triggerTypes, setTriggerTypes] = useState([]);
  const [showDetails, setShowDetails] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('session_token');
    const userId = localStorage.getItem('user_id');
    const userRole = localStorage.getItem('user_role');
    return {
      "X-Tenant-ID": tenantId || localStorage.getItem('current_tenant_slug') || "",
      "Content-Type": "application/json",
      ...(userId && { "X-User-ID": userId }),
      ...(userRole && { "X-User-Role": userRole }),
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  }, [tenantId]);

  // Charger les workflows
  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      // Charger les deux types de workflow
      const types = ["dossier_amo", "dossier_installation"];
      const allWorkflows = [];

      for (const type of types) {
        const res = await fetch(`${API}/api/workflow/definitions?workflow_type=${type}`, {
          headers: getAuthHeaders()
        });
        if (res.ok) {
          const data = await res.json();
          allWorkflows.push(...data.workflows.map(w => ({ ...w, workflow_type: type })));
        }
      }

      setWorkflows(allWorkflows);

      // Identifier le workflow actif
      const active = allWorkflows.find(w => w.status === "active");
      if (active) {
        setActiveWorkflow(active);
      }

    } catch (error) {
      console.error("Erreur chargement workflows:", error);
      toast.error("Erreur lors du chargement des workflows");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Charger le détail d'un workflow
  const loadWorkflowDetails = async (workflowId) => {
    try {
      const res = await fetch(`${API}/api/workflow/definitions/${workflowId}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedWorkflow(data);
        setShowDetails(true);
      }
    } catch (error) {
      console.error("Erreur chargement détails:", error);
      toast.error("Erreur lors du chargement des détails");
    }
  };

  // Charger les catalogues
  const loadCatalogues = useCallback(async () => {
    try {
      const [gardeFousRes, triggersRes] = await Promise.all([
        fetch(`${API}/api/workflow/garde-fous-catalogue`, { headers: getAuthHeaders() }),
        fetch(`${API}/api/workflow/triggers-types`, { headers: getAuthHeaders() })
      ]);

      if (gardeFousRes.ok) {
        const data = await gardeFousRes.json();
        setGardeFousCatalogue(data.garde_fous || []);
      }

      if (triggersRes.ok) {
        const data = await triggersRes.json();
        setTriggerTypes(data.triggers || []);
      }
    } catch (error) {
      console.error("Erreur chargement catalogues:", error);
    }
  }, [getAuthHeaders]);

  // Chargement initial
  useEffect(() => {
    loadWorkflows();
    loadCatalogues();
  }, [loadWorkflows, loadCatalogues]);

  // Valider un workflow
  const validateWorkflow = async (workflowId) => {
    try {
      const res = await fetch(`${API}/api/workflow/definitions/${workflowId}/validate`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        const data = await res.json();
        if (data.valid) {
          toast.success("Workflow validé avec succès");
        } else {
          toast.error(`Validation échouée: ${data.errors?.length || 0} erreur(s)`);
        }
        loadWorkflows();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erreur de validation");
      }
    } catch (error) {
      toast.error("Erreur lors de la validation");
    }
  };

  // Activer un workflow
  const activateWorkflow = async (workflowId) => {
    try {
      const res = await fetch(`${API}/api/workflow/definitions/${workflowId}/activate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({})
      });

      if (res.ok) {
        toast.success("Workflow activé avec succès");
        loadWorkflows();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Erreur d'activation");
      }
    } catch (error) {
      toast.error("Erreur lors de l'activation");
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtrer les workflows
  const filteredWorkflows = workflows.filter(w => {
    if (workflowType === "all") return true;
    return w.workflow_type === workflowType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
        <span className="ml-3 text-slate-400">Chargement des workflows...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="gouv-workflows">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <GitBranch className="w-6 h-6 text-purple-400" />
            Workflows Versionnés
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Gestion des cycles de vie des dossiers métier
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Select value={workflowType} onValueChange={setWorkflowType}>
            <SelectTrigger className="w-full max-w-[200px] bg-slate-900 border-slate-700 text-white">
              <SelectValue placeholder="Type de workflow" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-white">Tous les workflows</SelectItem>
              <SelectItem value="dossier_amo" className="text-white">Dossier AMO (TEF)</SelectItem>
              <SelectItem value="dossier_installation" className="text-white">Dossier Installation</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            onClick={loadWorkflows}
            className="border-slate-700 text-slate-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Workflow actif en vedette */}
      {activeWorkflow && (
        <Card className="bg-gradient-to-br from-purple-500/20 to-blue-500/20 border-purple-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-500/20 rounded-xl">
                  <Play className="w-8 h-8 text-purple-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-white">{activeWorkflow.nom}</h3>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                      v{activeWorkflow.version} - Actif
                    </Badge>
                  </div>
                  <p className="text-slate-300 text-sm">
                    {activeWorkflow.statuts_count} statuts • {activeWorkflow.transitions_count} transitions
                  </p>
                  <p className="text-slate-400 text-xs mt-1">
                    Activé le {formatDate(activeWorkflow.activated_at)}
                  </p>
                </div>
              </div>

              <Button
                onClick={() => loadWorkflowDetails(activeWorkflow.id)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Eye className="w-4 h-4 mr-2" />
                Voir les détails
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des workflows */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredWorkflows.map((workflow) => {
          const status = STATUS_COLORS[workflow.status] || STATUS_COLORS.draft;
          const isActive = workflow.status === "active";

          return (
            <Card 
              key={workflow.id}
              className={`bg-slate-800/50 border-slate-700 hover:border-slate-600 transition-colors ${
                isActive ? 'ring-2 ring-green-500/30' : ''
              }`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-white">{workflow.nom}</h4>
                      <Badge className={`${status.bg} ${status.text} ${status.border} border text-xs`}>
                        {status.label}
                      </Badge>
                      <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">
                        v{workflow.version}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-slate-400">
                      <span className="flex items-center gap-1">
                        <Activity className="w-3 h-3" />
                        {workflow.statuts_count} statuts
                      </span>
                      <span className="flex items-center gap-1">
                        <ArrowRight className="w-3 h-3" />
                        {workflow.transitions_count} transitions
                      </span>
                    </div>

                    <p className="text-xs text-slate-500 mt-2">
                      Type: <span className="text-slate-400">{workflow.workflow_type}</span>
                      {workflow.created_at && (
                        <> • Créé le {formatDate(workflow.created_at)}</>
                      )}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => loadWorkflowDetails(workflow.id)}
                      className="text-slate-400 hover:text-white"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>

                    {workflow.status === "draft" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => validateWorkflow(workflow.id)}
                        className="text-blue-400 hover:text-blue-300"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    )}

                    {workflow.status === "validated" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => activateWorkflow(workflow.id)}
                        className="text-green-400 hover:text-green-300"
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredWorkflows.length === 0 && (
        <Card className="bg-slate-800/30 border-slate-700">
          <CardContent className="p-8 text-center">
            <GitBranch className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Aucun workflow trouvé pour ce filtre</p>
          </CardContent>
        </Card>
      )}

      {/* Dialog détails du workflow */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-purple-400" />
              {selectedWorkflow?.nom}
              <Badge className="ml-2">v{selectedWorkflow?.version}</Badge>
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {selectedWorkflow?.description || "Définition complète du workflow"}
            </DialogDescription>
          </DialogHeader>

          {selectedWorkflow && (
            <div className="space-y-6">
              {/* Infos générales */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400">Statut</p>
                  <Badge className={`mt-1 ${STATUS_COLORS[selectedWorkflow.status]?.bg} ${STATUS_COLORS[selectedWorkflow.status]?.text}`}>
                    {STATUS_COLORS[selectedWorkflow.status]?.label}
                  </Badge>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400">Tenant</p>
                  <p className="text-white font-medium">{selectedWorkflow.tenant_id}</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400">Type</p>
                  <p className="text-white font-medium">{selectedWorkflow.type}</p>
                </div>
              </div>

              {/* Statuts */}
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-purple-400" />
                  Statuts ({selectedWorkflow.statuts?.length || 0})
                </h4>
                <div className="space-y-2">
                  {selectedWorkflow.statuts?.map((statut, idx) => (
                    <Collapsible
                      key={idx}
                      open={expandedStatuts[statut.code]}
                      onOpenChange={(open) => setExpandedStatuts({ ...expandedStatuts, [statut.code]: open })}
                    >
                      <CollapsibleTrigger className="w-full">
                        <div className={`flex items-center justify-between p-3 rounded-lg bg-slate-800/50 hover:bg-slate-800 ${
                          statut.est_initial ? 'border-l-4 border-green-500' : 
                          statut.est_final ? 'border-l-4 border-red-500' : ''
                        }`}>
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: statut.couleur || '#6b7280' }}
                            />
                            <span className="text-white font-medium">{statut.nom}</span>
                            <span className="text-slate-500 text-sm">({statut.code})</span>
                            {statut.est_initial && (
                              <Badge className="bg-green-500/20 text-green-400 text-xs">Initial</Badge>
                            )}
                            {statut.est_final && (
                              <Badge className="bg-red-500/20 text-red-400 text-xs">Final</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">
                              Phase: {statut.phase}
                            </Badge>
                            {expandedStatuts[statut.code] ? (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400" />
                            )}
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="ml-6 mt-2 p-3 bg-slate-800/30 rounded-lg text-sm">
                          {statut.description && (
                            <p className="text-slate-300 mb-2">{statut.description}</p>
                          )}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-400">
                            <div>Hub: <span className="text-white">{statut.hub}</span></div>
                            <div>Ordre: <span className="text-white">{statut.ordre}</span></div>
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              </div>

              {/* Transitions */}
              <div>
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-blue-400" />
                  Transitions ({selectedWorkflow.transitions?.length || 0})
                </h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {selectedWorkflow.transitions?.map((transition, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-slate-300">{transition.de}</span>
                        <ArrowRight className="w-4 h-4 text-slate-500" />
                        <span className="text-white font-medium">{transition.vers}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {transition.trigger && transition.trigger !== "manual" && (
                          <Badge className="bg-purple-500/20 text-purple-400 text-xs">
                            Trigger: {transition.trigger}
                          </Badge>
                        )}
                        {transition.roles_autorises?.length > 0 && (
                          <Badge variant="outline" className="border-slate-600 text-slate-400 text-xs">
                            {transition.roles_autorises.join(", ")}
                          </Badge>
                        )}
                        {transition.garde_fous?.length > 0 && (
                          <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                            {transition.garde_fous.length} garde-fou(s)
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Garde-fous définis */}
              {selectedWorkflow.garde_fous_definitions?.length > 0 && (
                <div>
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-400" />
                    Garde-fous ({selectedWorkflow.garde_fous_definitions?.length || 0})
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {selectedWorkflow.garde_fous_definitions?.map((gf, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-slate-800/50">
                        <p className="text-white font-medium">{gf.nom}</p>
                        <p className="text-slate-400 text-sm">{gf.code}</p>
                        {gf.description && (
                          <p className="text-slate-500 text-xs mt-1">{gf.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetails(false)} className="border-slate-600">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Catalogue des triggers */}
      <Card className="bg-slate-800/30 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-cyan-400" />
            Triggers automatiques disponibles
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {triggerTypes.map((trigger) => (
              <div key={trigger.code} className="p-3 rounded-lg bg-slate-800/50">
                <p className="text-white font-medium text-sm">{trigger.code}</p>
                <p className="text-slate-400 text-xs mt-1">{trigger.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Catalogue des garde-fous */}
      <Card className="bg-slate-800/30 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-400" />
            Catalogue des garde-fous
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {gardeFousCatalogue.map((gf) => (
              <div key={gf.code} className="p-3 rounded-lg bg-slate-800/50">
                <p className="text-white font-medium">{gf.nom}</p>
                <p className="text-slate-500 text-xs font-mono">{gf.code}</p>
                <p className="text-slate-400 text-sm mt-1">{gf.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
